import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { getVersion } from '../utils/version';

const DISMISSED_VERSION_KEY = 'dismissedAppVersion';
const VERSION_FILE_URL = '/version.json';
const CACHE_CLEAR_TIMEOUT = 100;

const clearAppCacheAndReload = async () => {
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
    }

    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch (error) {
    console.warn('Failed to clear caches during update refresh:', error);
  }

  setTimeout(() => {
    window.location.reload();
  }, CACHE_CLEAR_TIMEOUT);
};

const fetchLatestVersion = async (): Promise<string | null> => {
  try {
    const response = await fetch(`${VERSION_FILE_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return null;
    }

    const payload = await response.json();
    return payload?.version || null;
  } catch (error) {
    console.warn('Unable to fetch latest app version:', error);
    return null;
  }
};

const UpdatePrompt: React.FC = () => {
  const { t } = useTranslation();
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const checkVersion = async () => {
      const currentVersion = getVersion();
      const dismissedVersion = localStorage.getItem(DISMISSED_VERSION_KEY);
      const newestVersion = await fetchLatestVersion();

      if (!newestVersion || newestVersion === currentVersion) {
        return;
      }

      if (dismissedVersion === newestVersion) {
        return;
      }

      setLatestVersion(newestVersion);
      setOpen(true);
    };

    checkVersion();
  }, []);

  const handleClose = () => {
    if (latestVersion) {
      localStorage.setItem(DISMISSED_VERSION_KEY, latestVersion);
    }
    setOpen(false);
  };

  const handleUpdate = async () => {
    await clearAppCacheAndReload();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('common.updateApp')}
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body1">
            {t('common.newVersionAvailable')}
          </Typography>
          {latestVersion && (
            <Typography variant="caption" color="text.secondary">
              {t('common.latestVersionInfo', { version: latestVersion })}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {t('common.currentVersionInfo', { version: getVersion() })}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>{t('common.dismissUpdate')}</Button>
        <Button variant="contained" onClick={handleUpdate}>
          {t('common.refresh')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdatePrompt;
